/**
 * Typed fetch wrapper. Reads the API base from VITE_API_BASE (or proxies via /api).
 * Attaches the bearer token from localStorage and normalizes errors.
 */

import {
  coerceConversation,
  coerceMessage,
  normalizeConversationsList,
  normalizeConversationDetail,
  normalizeMessages,
  normalizePollCreate,
  normalizePollDetail,
  normalizeReactions,
  normalizeMessageReadBy,
  normalizeUser,
  normalizeUserProfile,
  normalizeUsers,
} from './adapters';

export interface User {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  lastSeen: string | null;
}

export type ConversationType = 'direct' | 'group';

export interface Conversation {
  id: string;
  type: ConversationType;
  name: string | null;
  imageUrl: string | null;
  ownerId?: string | null;
  lastMessage?: Message | null;
  unreadCount?: number;
  members?: ConversationMember[];
  createdAt: string;
  updatedAt: string;
}

export interface ConversationMember {
  userId: string;
  role: 'owner' | 'manager' | 'member';
  user?: User;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  sender?: User | null;
  content: string;
  type: 'text' | 'poll' | 'system';
  replyToId?: string | null;
  replyTo?: Message | null;
  poll?: Poll | null;
  createdAt: string;
  editedAt?: string | null;
  deletedAt?: string | null;
}

export interface Poll {
  id: string;
  messageId: string;
  question: string;
  allowMultiple: boolean;
  options: PollOption[];
  userVotes: string[];
}

export interface PollOption {
  id: string;
  text: string;
  voteCount: number;
}

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

/* -------------------------------------------------------------------------- */
/*  Reactions (F-007)                                                          */
/* -------------------------------------------------------------------------- */

export interface Reaction {
  emoji: string;
  count: number;
  myReacted: boolean;
}

/* -------------------------------------------------------------------------- */
/*  Notification preferences (F-006)                                           */
/* -------------------------------------------------------------------------- */

export type NotificationLevel = 'all' | 'mentions' | 'off';
export interface NotificationPref {
  level: NotificationLevel;
}

/* -------------------------------------------------------------------------- */
/*  User profile (F-008)                                                       */
/* -------------------------------------------------------------------------- */

/** Public profile of a user. Adds `createdAt` to the `User` shape. */
export interface UserProfile extends User {
  createdAt: string;
}

/* -------------------------------------------------------------------------- */
/*  Read receipts (F-009)                                                      */
/* -------------------------------------------------------------------------- */

export interface MessageReadBy {
  userId: string;
  username: string;
  displayName: string;
  lastReadMessageId: string | null;
}

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const TOKEN_KEY = 'cf-chat:token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  skipAuth?: boolean;
  headers?: Record<string, string>;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(opts.headers ?? {}),
  };

  if (!opts.skipAuth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  // Surface long-running requests to the global loading bar so users on
  // slow networks have visible feedback. We start at the SLOW_THRESHOLD so
  // fast responses don't flicker the bar.
  const SLOW_THRESHOLD_MS = 250;
  let slowTimer: number | null = null;
  const startLoading = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('loading:start'));
    }
  };
  const endLoading = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('loading:end'));
    }
  };
  slowTimer = window.setTimeout(() => {
    slowTimer = null;
    startLoading();
  }, SLOW_THRESHOLD_MS);

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body,
      signal: opts.signal,
    });
  } catch (err) {
    if (slowTimer != null) {
      clearTimeout(slowTimer);
      slowTimer = null;
    } else {
      endLoading();
    }
    const e: ApiError = {
      status: 0,
      code: 'network_error',
      message: err instanceof Error ? err.message : 'Network error',
    };
    throw e;
  }
  if (slowTimer != null) {
    clearTimeout(slowTimer);
    slowTimer = null;
  } else {
    endLoading();
  }

  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      try {
        payload = await res.text();
      } catch {
        payload = null;
      }
    }
    // API errors use either `message` (custom routes) or `error` (jsonError helper).
    // Accept both, prefer `message` if both are present.
    const payloadObj = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
    const rawMessage = payloadObj && typeof payloadObj.message === 'string'
      ? payloadObj.message
      : payloadObj && typeof payloadObj.error === 'string'
        ? payloadObj.error
        : null;
    const message = rawMessage ?? `Request failed with status ${res.status}`;
    const code =
      payloadObj && typeof payloadObj.code === 'string'
        ? payloadObj.code
        : 'http_error';
    const err: ApiError = {
      status: res.status,
      code,
      message,
      details: payloadObj ?? undefined,
    };
    throw err;
  }

  if (res.status === 204) return undefined as unknown as T;

  const contentType = res.headers.get('Content-Type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = API_BASE + path;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/* ----- Endpoint helpers ----- */

export interface AuthResponse {
  user: User;
  token: string;
}

export const Auth = {
  register: async (data: { username: string; password: string; displayName?: string }) => {
    const res = await api<{ user: unknown; token: string }>('/api/auth/register', { method: 'POST', body: data, skipAuth: true });
    return { user: normalizeUser(res), token: res.token };
  },
  login: async (data: { username: string; password: string }) => {
    const res = await api<{ user: unknown; token: string }>('/api/auth/login', { method: 'POST', body: data, skipAuth: true });
    return { user: normalizeUser(res), token: res.token };
  },
  logout: () => api<void>('/api/auth/logout', { method: 'POST' }),
  me: async () => {
    const res = await api<{ user: unknown }>('/api/users/me');
    return { user: normalizeUser(res) };
  },
};

export const Users = {
  search: async (q: string, signal?: AbortSignal) => {
    const res = await api<{ users?: unknown[] }>('/api/users/search', { query: { q }, signal });
    return { users: normalizeUsers(res) };
  },
};

export const Conversations = {
  list: async () => {
    const res = await api<{ conversations?: unknown[] }>('/api/conversations');
    return { conversations: normalizeConversationsList(res) };
  },
  get: async (id: string) => {
    const res = await api<unknown>(`/api/conversations/${encodeURIComponent(id)}`);
    const norm = normalizeConversationDetail(res);
    if (!norm) throw { status: 404, code: 'NOT_FOUND', message: 'conversation not found' };
    return norm;
  },
  create: async (data: { type: ConversationType; name?: string; userIds: string[] }) => {
    const res = await api<{ conversation?: unknown; reused?: boolean }>('/api/conversations', { method: 'POST', body: data });
    return { conversation: coerceConversation(res.conversation ?? res), reused: Boolean(res.reused) };
  },
  update: async (id: string, data: { name?: string; imageUrl?: string }) => {
    const res = await api<{ conversation?: unknown }>(`/api/conversations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: data,
    });
    return { conversation: coerceConversation(res.conversation ?? res) };
  },
  addMember: (id: string, userId: string) =>
    api<{ ok: true; role: 'owner' | 'manager' | 'member' }>(
      `/api/conversations/${encodeURIComponent(id)}/members`,
      { method: 'POST', body: { userId } },
    ),
  removeMember: (id: string, userId: string) =>
    api<{ ok: true }>(`/api/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),
  updateMemberRole: (id: string, userId: string, role: 'manager' | 'member') =>
    api<{ ok: true; role: 'owner' | 'manager' | 'member' }>(
      `/api/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: { role } },
    ),
  messages: async (id: string, opts: { before?: string; limit?: number; signal?: AbortSignal } = {}) => {
    const res = await api<{ messages?: unknown[] }>(`/api/conversations/${encodeURIComponent(id)}/messages`, {
      query: { before: opts.before, limit: opts.limit ?? 50 },
      signal: opts.signal,
    });
    return { messages: normalizeMessages(res) };
  },
  sendMessage: async (id: string, data: { content: string; replyToId?: string }) => {
    const res = await api<{ message?: unknown }>(`/api/conversations/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: data,
    });
    return { message: coerceMessage(res.message ?? res) };
  },
  markRead: (id: string, messageId: string) =>
    api<void>(`/api/conversations/${encodeURIComponent(id)}/read`, {
      method: 'POST',
      body: { messageId },
    }),
  createPoll: async (id: string, data: { question: string; options: string[]; allowMultiple?: boolean }) => {
    const res = await api<unknown>(`/api/conversations/${encodeURIComponent(id)}/polls`, {
      method: 'POST',
      body: data,
    });
    const norm = normalizePollCreate(res);
    if (!norm) throw { status: 400, code: 'BAD_REQUEST', message: 'invalid poll response' };
    return norm;
  },
};

export const Polls = {
  get: async (id: string) => {
    const res = await api<unknown>(`/api/polls/${encodeURIComponent(id)}`);
    const poll = normalizePollDetail(res);
    if (!poll) throw { status: 404, code: 'NOT_FOUND', message: 'poll not found' };
    return poll;
  },
  vote: (id: string, optionIds: string[]) =>
    api<{ ok: true }>(`/api/polls/${encodeURIComponent(id)}/vote`, { method: 'POST', body: { optionIds } }),
};

export const Presence = {
  heartbeat: () => api<void>('/api/presence/heartbeat', { method: 'POST' }),
  get: async (userId: string) => {
    const res = await api<{ online?: unknown; lastSeen?: unknown }>(
      `/api/presence/${encodeURIComponent(userId)}`,
    );
    return {
      online: Boolean(res.online),
      lastSeen:
        typeof res.lastSeen === 'string'
          ? res.lastSeen
          : typeof res.lastSeen === 'number'
            ? new Date(res.lastSeen).toISOString()
            : null,
    };
  },
};

export const LinkPreview = {
  fetch: (url: string, signal?: AbortSignal) =>
    api<{ url: string; siteName?: string; title?: string; description?: string; image?: string }>(
      '/api/link-preview',
      { query: { url }, signal },
    ),
};

/* -------------------------------------------------------------------------- */
/*  Endpoint helpers — new feature namespaces                                  */
/* -------------------------------------------------------------------------- */

/**
 * F-007 — Emoji reactions.
 * `toggle` flips a single (messageId, emoji) reaction for the caller; the
 * server returns the canonical post-state list. `get` returns the current
 * reaction list for a single message (used to lazy-load when the user opens
 * the detail popover).
 */
export const Reactions = {
  toggle: async (id: string, emoji: string) => {
    const res = await api<{ reactions?: unknown[] }>(
      `/api/messages/${encodeURIComponent(id)}/reactions`,
      { method: 'POST', body: { emoji } },
    );
    return { reactions: normalizeReactions(res) };
  },
  get: async (id: string) => {
    const res = await api<{ reactions?: unknown[] }>(
      `/api/messages/${encodeURIComponent(id)}/reactions`,
    );
    return { reactions: normalizeReactions(res) };
  },
};

/**
 * F-006 — Per-conversation notification preferences.
 * Stored only when the user changes them; the implicit default is 'all'.
 */
export const NotificationPrefs = {
  get: async (cid: string) => {
    const res = await api<{ level?: NotificationLevel }>(
      `/api/conversations/${encodeURIComponent(cid)}/notification-pref`,
    );
    const level: NotificationLevel =
      res.level === 'mentions' || res.level === 'off' ? res.level : 'all';
    return { level };
  },
  set: (cid: string, level: NotificationLevel) =>
    api<{ level: NotificationLevel }>(
      `/api/conversations/${encodeURIComponent(cid)}/notification-pref`,
      { method: 'PUT', body: { level } },
    ),
};

/**
 * F-008 — Public user profile (lookup by username).
 * The route is auth-required but member-safe: only the public profile fields
 * are returned. The current user's own profile (`Auth.me`) is still the
 * canonical source for self.
 */
export const Profiles = {
  get: async (username: string) => {
    const res = await api<{ user?: unknown }>(
      `/api/users/${encodeURIComponent(username)}`,
    );
    return { user: normalizeUserProfile(res) };
  },
};

/**
 * F-009 — Read receipts: who has read a given message.
 *
 * NOTE: this is a NEW namespace and does NOT collide with
 * `Conversations.messages` (the listing endpoint). The server returns users
 * whose `last_read_message_id >= messageId`.
 */
export const Messages = {
  readBy: async (id: string) => {
    const res = await api<{ readBy?: unknown[] }>(
      `/api/messages/${encodeURIComponent(id)}/read-by`,
    );
    return { readBy: normalizeMessageReadBy(res) };
  },
};
